import type { MarketRunBudget } from './index';
import { MarketIntelligenceResponseError } from './index';
import { HISTORY_WINDOWS, type HistoryWindow } from './collectionHistory';
import { isDiscoveryDateCapability } from './discoveryDates';
import { NATIVE_HISTORY_SUPPORT, normalizeNativeDateHistory } from './nativeDateHistory';
import { COMMENT_HISTORY_CAPABILITY, COMMENT_HISTORY_SUPPORT, normalizeCommentHistory } from './commentHistory';

export const HISTORY_CONFIRMATION_VERSION = 'market-history-confirmation/v1';
export const NATIVE_HISTORY_CONFIRMATION_VERSION = 'market-native-date-history-confirmation/v1';
export const COMMENT_HISTORY_CONFIRMATION_VERSION = 'market-comment-history-confirmation/v1';
export const HISTORY_CONFIRMATION_ACTION = 'restart_from_current_scope';
export const HISTORY_CONFIRMATION_BLOCKS = ['history_submit_outcome_unresolved', 'history_attempt_in_flight', 'no_unacknowledged_history'] as const;
interface CommonConfirmationScope {
    action: typeof HISTORY_CONFIRMATION_ACTION;
    monitor_id: string;
    monitor_version: number;
    slice_id: string;
    input: string;
    run_budget: MarketRunBudget;
    source_record_limit: number;
    acknowledged_attempts: number;
    retained_attempts: number;
    preview_hash: string;
    coverage_semantics: 'historical_gaps_retained_unrepaired';
    run_created: false;
}
type HistoryConfirmationScope = CommonConfirmationScope & ({ contract_version: typeof HISTORY_CONFIRMATION_VERSION;
    initial_window: HistoryWindow; max_window: HistoryWindow }
    | { contract_version: typeof NATIVE_HISTORY_CONFIRMATION_VERSION; capability: string;
        initial_start_date: string; initial_end_date: string; overlap_days: number; max_window_days: number }
    | { contract_version: typeof COMMENT_HISTORY_CONFIRMATION_VERSION; capability: typeof COMMENT_HISTORY_CAPABILITY;
        initial_days_back: number; max_days_back: number; overlap_hours: number });
export type HistoryConfirmationPreview = HistoryConfirmationScope & {
    can_confirm: boolean;
    blocking_reason?: typeof HISTORY_CONFIRMATION_BLOCKS[number];
}
export type HistoryConfirmationReceipt = HistoryConfirmationScope & {
    confirmation_id: string;
    confirmed_at: string;
}
export interface HistoryConfirmationIntent { id: string; preview: HistoryConfirmationPreview }
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const integer = (value: unknown, minimum: number) => Number.isSafeInteger(value) && Number(value) >= minimum;
export const validHistoryConfirmationId = (value: unknown): value is string => typeof value === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
const invalid = () => new MarketIntelligenceResponseError('history confirmation');
const scopeKeys = ['contract_version', 'action', 'monitor_id', 'monitor_version', 'slice_id', 'input',
    'run_budget', 'source_record_limit', 'acknowledged_attempts', 'retained_attempts', 'preview_hash', 'coverage_semantics', 'run_created'];
const dateKeys = ['capability', 'initial_start_date', 'initial_end_date', 'overlap_days', 'max_window_days'];
const commentKeys = ['capability', 'initial_days_back', 'max_days_back', 'overlap_hours'];
const keysFor = (value: Record<string, unknown>) => [...scopeKeys, ...(value.contract_version === NATIVE_HISTORY_CONFIRMATION_VERSION
    ? dateKeys : value.contract_version === COMMENT_HISTORY_CONFIRMATION_VERSION ? commentKeys : ['initial_window', 'max_window'])];
function validRange(value: Record<string, unknown>): boolean {
    if (value.contract_version === HISTORY_CONFIRMATION_VERSION) return HISTORY_WINDOWS.includes(value.initial_window as HistoryWindow)
        && HISTORY_WINDOWS.includes(value.max_window as HistoryWindow)
        && HISTORY_WINDOWS.indexOf(value.initial_window as HistoryWindow) <= HISTORY_WINDOWS.indexOf(value.max_window as HistoryWindow);
    try {
        if (value.contract_version === COMMENT_HISTORY_CONFIRMATION_VERSION) return value.capability === COMMENT_HISTORY_CAPABILITY
            && !!normalizeCommentHistory({ schema_version: COMMENT_HISTORY_SUPPORT, mode: 'adaptive_recheck',
                overlap_hours: value.overlap_hours, max_days_back: value.max_days_back }, value.initial_days_back);
        return value.contract_version === NATIVE_HISTORY_CONFIRMATION_VERSION && typeof value.capability === 'string' && isDiscoveryDateCapability(value.capability)
            && !!normalizeNativeDateHistory({ schema_version: NATIVE_HISTORY_SUPPORT, mode: 'incremental_recheck',
                overlap_days: value.overlap_days, max_window_days: value.max_window_days }, {
                discovery_start_date: value.initial_start_date as string, discovery_end_date: value.initial_end_date as string });
    } catch { return false; }
}
function validScope(value: unknown, monitor: string, slice: string, extra: string[]): value is HistoryConfirmationScope & Record<string, unknown> {
    if (!record(value) || Object.keys(value).some(key => ![...keysFor(value), ...extra].includes(key))) return false;
    const budget = value.run_budget;
    return validRange(value) && value.action === HISTORY_CONFIRMATION_ACTION
        && value.monitor_id === monitor && value.slice_id === slice && !!slice.trim() && slice.length <= 128
        && integer(value.monitor_version, 1) && typeof value.input === 'string' && !!value.input.trim()
        && record(budget) && Object.keys(budget).length === 3 && budget.contract_version === 'market-run-budget/v1'
        && budget.unit === 'unique_canonical_records' && integer(budget.max_records, 1)
        && integer(value.source_record_limit, 1) && integer(value.acknowledged_attempts, 0) && integer(value.retained_attempts, 0)
        && Number(value.acknowledged_attempts) <= Number(value.retained_attempts) && Number(value.retained_attempts) <= 5000
        && typeof value.preview_hash === 'string' && /^[a-f0-9]{64}$/.test(value.preview_hash)
        && value.coverage_semantics === 'historical_gaps_retained_unrepaired' && value.run_created === false;
}
export function validateHistoryConfirmationPreview(value: unknown, monitor: string, slice: string): HistoryConfirmationPreview {
    if (!validScope(value, monitor, slice, ['can_confirm', 'blocking_reason']) || typeof value.can_confirm !== 'boolean'
        || (value.can_confirm ? value.blocking_reason !== undefined || value.acknowledged_attempts === 0
            : !HISTORY_CONFIRMATION_BLOCKS.includes(value.blocking_reason as typeof HISTORY_CONFIRMATION_BLOCKS[number]))) throw invalid();
    return value as unknown as HistoryConfirmationPreview;
}
export function validateHistoryConfirmationReceipt(value: unknown, monitor: string, intent: HistoryConfirmationIntent): HistoryConfirmationReceipt {
    const expected = validateHistoryConfirmationPreview(intent.preview, monitor, intent.preview.slice_id);
    if (!expected.can_confirm || !validHistoryConfirmationId(intent.id)
        || !validScope(value, monitor, expected.slice_id, ['confirmation_id', 'confirmed_at'])
        || value.confirmation_id !== intent.id || typeof value.confirmed_at !== 'string'
        || !/(?:Z|[+-]\d{2}:\d{2})$/.test(value.confirmed_at) || !Number.isFinite(Date.parse(value.confirmed_at))) throw invalid();
    for (const key of keysFor(value)) {
        if (key === 'run_budget') {
            if (value.run_budget.max_records !== expected.run_budget.max_records) throw invalid();
        } else if (value[key] !== (expected as unknown as Record<string, unknown>)[key]) throw invalid();
    }
    return value as unknown as HistoryConfirmationReceipt;
}
export function historyConfirmationWrite(intent: HistoryConfirmationIntent) {
    const preview = validateHistoryConfirmationPreview(intent.preview, intent.preview.monitor_id, intent.preview.slice_id);
    if (!preview.can_confirm || !validHistoryConfirmationId(intent.id)) throw invalid();
    return { slice_id: preview.slice_id, action: HISTORY_CONFIRMATION_ACTION, preview_hash: preview.preview_hash, acknowledge_unrepaired: true };
}
